import { useMutation, useQueryClient } from '@tanstack/react-query';

import { translate } from '@/i18n';
import { useFetchUserData } from '@/modules/Auth/hooks/useFetchUserData.ts';
import { AssetsAPINamespace } from '@/shared/api/assets';
import { notifyService } from '@/shared/services/notify.service.tsx';
import { useGlobalStore } from '@/shared/store/store.ts';
import { QUERY_KEY } from '@/shared/utils/constants.ts';

import { AssetBody } from '@/shared/types/assets/assets.types.ts';

export const useUpdateCraneLoadedStatus = () => {
  const queryClient = useQueryClient();
  const site = useGlobalStore(store => store.site);
  const { user } = useFetchUserData();

  return useMutation({
    mutationFn: async (args: { assetResName: string; loaded: boolean }) => {
      // Get the current asset to preserve existing extra properties
      // Use getQueriesData to find all equipment queries (there are multiple queries per category)
      const equipmentQueries = queryClient.getQueriesData<AssetBody[]>({
        queryKey: [QUERY_KEY.EQUIPMENTS_GET],
      });

      // Find the current asset across all equipment queries
      let currentAsset: AssetBody | undefined;
      for (const [, data] of equipmentQueries) {
        if (data) {
          currentAsset = data.find(asset => asset.assetResName === args.assetResName);
          if (currentAsset) break;
        }
      }

      // Update the loaded property in extraProperties (convert boolean to string)
      // extraProperties is {key: string, value: string}[]
      const updatedExtraProperties = [
        ...(currentAsset?.extraProperties?.filter(prop => prop.key !== 'loaded') ?? []),
        { key: 'loaded', value: String(args.loaded) },
      ];

      const result = await AssetsAPINamespace.updateOne({
        accessToken: user?.accessToken ?? '',
        accountResName: user?.accountResourceName ?? '',
        assetResName: args.assetResName,
        extraProperties: updatedExtraProperties,
        siteResName: site,
      });

      return result;
    },

    mutationKey: [QUERY_KEY.EQUIPMENT_UPDATE],

    // Optimistically update the cache BEFORE the mutation
    onMutate: async (args: { assetResName: string; loaded: boolean }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY.EQUIPMENTS_GET],
      });

      // Get all equipment queries
      const equipmentQueries = queryClient.getQueriesData<AssetBody[]>({
        queryKey: [QUERY_KEY.EQUIPMENTS_GET],
      });

      // Store previous values for rollback on error
      const previousQueries = new Map();

      // Update each query that contains the asset
      for (const [queryKey, data] of equipmentQueries) {
        if (data) {
          const assetIndex = data.findIndex(asset => asset.assetResName === args.assetResName);
          if (assetIndex !== -1) {
            // Store previous data for rollback (deep copy to ensure proper rollback)
            previousQueries.set(queryKey, JSON.parse(JSON.stringify(data)));

            // Create updated data with new loaded status
            const updatedData = [...data];
            const updatedAsset = {
              ...updatedData[assetIndex],
              extraProperties: [
                ...(updatedData[assetIndex].extraProperties?.filter(
                  prop => prop.key !== 'loaded',
                ) ?? []),
                { key: 'loaded', value: String(args.loaded) },
              ],
            };
            updatedData[assetIndex] = updatedAsset;

            // Optimistically update the cache
            queryClient.setQueryData<AssetBody[]>(queryKey, updatedData);
          }
        }
      }

      // Return context with previous values for rollback
      return { previousQueries };
    },

    onError: (error, _args, context) => {
      // Rollback optimistic updates on error - this will revert the UI to its original state
      if (context?.previousQueries) {
        context.previousQueries.forEach((previousData, queryKey) => {
          queryClient.setQueryData(queryKey, previousData);
        });
      }
      // Show error toast notification
      notifyService.error(translate('cranes.updateFailed'));
      console.error('Failed to update crane status:', error);
    },

    onSuccess: () => {
      // Invalidate queries in the background to ensure consistency
      // Don't await - this allows the mutation to complete immediately
      // The UI is already updated optimistically, so refetches happen in background
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.EQUIPMENTS_GET],
      });
    },
  });
};
